import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import sdkClient from 'utils/sdk-client';
import Log from 'utils/log';
import { getCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import { ChannelReachBackRequestStatus } from 'constants/reach-back-requests-constants';

const initialState = {
    requests: [],
    channelsCount: 0,
};

class ReachBackRequestsSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!ReachBackRequestsSliceHandler.instance) {
            ReachBackRequestsSliceHandler.instance = new ReachBackRequestsSliceHandler();
        }

        return ReachBackRequestsSliceHandler.instance;
    }

    constructor() {
        super('reachBackRequestsSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_LOADED, () => {
            this.onCompanySettingsLoaded();
        });

        sdkClient.setSocketEventListener('CHANNEL_REACH_BACK_REQUEST', (reachBackRequest) =>
            this.onNewReachBackRequest(reachBackRequest),
        );

        sdkClient.setSocketEventListener('CHANNEL_REACH_BACK_REQUEST_LIST_RESPONSE', (reachBackRequests) =>
            this.onReachBackRequestListResponse(reachBackRequests),
        );

        /**
         * Flag which allows knowing if the initial list cleaning has been perfpormed.
         * @type {boolean}
         */
        this.initialLoading = false;

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                addReachBackRequest(state, action) {
                    state.requests = [...state.requests, action.payload];
                },
                updateReachBackRequestsList(state, action) {
                    state.requests = action.payload;
                },
                updateReachBackRequest(state, action) {
                    state.requests = action.payload;
                },
                updateConnectedChannelsCount(state, action) {
                    state.channelsCount = action.payload;
                },
            },
        });
    }

    onCompanySettingsLoaded = () => {
        const companySettings = getCompanySettings();
        // make a request to obtain the total number of RBR which must be cleaned upon login
        if (companySettings.AllowChannelReachBackRequest) {
            this.requestNotificationsBatch(0, 1);
        }
    };

    /**
     * Resolver after initial requests are received
     * @param {ReachBackRequestListResponse} listResponse
     */
    onReachBackRequestListResponse = (listResponse) => {
        if (listResponse.totalNumberOfMessages === 0) {
            Log.notice('REACH BACK STORE', 'No RBR to be cleaned upon login');
            return;
        }

        if (!this.initialLoading) {
            // load ALL requests which must be removed upon login
            this.requestNotificationsBatch(0, listResponse.totalNumberOfMessages);
            this.initialLoading = true;
        } else {
            this.deleteAllRequests(listResponse.messages);
        }
    };

    requestNotificationsBatch = (offset, limit) => {
        sdkClient.emitSocketEvent('CHANNEL_REACH_BACK_LIST_REQUEST', {
            offset,
            limit,
        });
    };

    /**
     * Handle an incoming request
     * @param {ReachBackRequest} reachBackRequest
     */
    onNewReachBackRequest = (reachBackRequest) => {
        if (typeof reachBackRequest.wasMessageRead === 'undefined') {
            reachBackRequest.wasMessageRead = false;
        }

        Log.debug(
            'CH.REACH BACK STORE',
            `Received new ${reachBackRequest.content.Urgent ? 'URGENT' : 'REGULAR'} request from ${reachBackRequest.msisdn}`,
        );

        this.dispatch('addReachBackRequest', reachBackRequest);
    };

    /**
     * Clears all requests received
     * @param {ReachBackRequest[]} requests
     */
    deleteAllRequests = (requests) => {
        if (requests.length === 0) {
            return;
        }

        const messagesIds = [];
        requests.forEach((request) => {
            messagesIds.push(request.messageId);
        });
        Log.notice('CH.REACH BACK STORE', `Deleting all requests (${messagesIds.length})`);

        sdkClient.emitSocketEvent('CHANNEL_REACH_BACK_READ_REQUEST', {
            messageIds: messagesIds,
            notificationStatus: ChannelReachBackRequestStatus.DELETED,
        });
    };

    /**
     * Clears all requests from a specific channel once that channel is closed
     * @param {number} channelId
     */
    deleteRequestsByChannel = (channelId) => {
        if (typeof channelId === 'undefined' || channelId === null) {
            return;
        }

        const messagesIds = [];
        const remainingRequests = [];
        const requests = this.getOwnState().requests;

        if (requests.length > 0) {
            requests.forEach((request) => {
                if (request.content.GroupId !== channelId) {
                    remainingRequests.push(request);
                    return;
                }
                messagesIds.push(request.messageId);
            });
        }

        if (messagesIds.length === 0) {
            return;
        }

        this.dispatch('updateReachBackRequest', remainingRequests);

        Log.notice(
            'CH.REACH BACK STORE',
            `Deleting all requests (${messagesIds.length}) which are linked with channel ${channelId}`,
        );

        sdkClient.emitSocketEvent('CHANNEL_REACH_BACK_READ_REQUEST', {
            messageIds: messagesIds,
            notificationStatus: ChannelReachBackRequestStatus.DELETED,
        });
    };

    /**
     * Delete a specific request
     * @param {number} requestId
     */
    deleteRequest = (requestId) => {
        const requests = this.getOwnState().requests;
        const updatedRequestsList = requests.filter((request) => request.messageId !== requestId);

        if (!updatedRequestsList.length === requests.length) {
            Log.warn('CH.REACH BACK STORE', `Cannot delete the request "${requestId}" because it was not found`);
            return;
        }

        this.dispatch('updateReachBackRequestsList', updatedRequestsList);

        sdkClient.emitSocketEvent('CHANNEL_REACH_BACK_READ_REQUEST', {
            messageIds: [requestId],
            notificationStatus: ChannelReachBackRequestStatus.DELETED,
        });
    };

    /**
     * The PTT button has been pressed, so must request floor in the designated channel.
     * @param {number} requestId
     */
    requestFloor = (requestId) => {
        const requests = this.getOwnState().requests;
        /**
         * @type {ReachBackRequest}
         */
        const updatedRequest = requests.filter((request) => request.messageId === requestId)[0];

        Log.debug(
            'CH.REACH BACK STORE',
            `Floor has been requested in channel ${updatedRequest.content.GroupId} with RBR "${requestId}" `,
        );

        LocalEvent.trigger(LocalEvent.eventTypes.CHANNEL_REACH_BACK_REQUESTS_PTT, updatedRequest);

        this.deleteRequest(requestId);
    };

    setConnectedChannelsCount = (count) => {
        this.dispatch('updateConnectedChannelsCount', count);
    };
}

export const reachBackRequestsSliceHandler = ReachBackRequestsSliceHandler.getInstance();
export const selectReachBackRequests = (state) => state.reachBackRequestsSlice;
export const { deleteRequest, deleteRequestsByChannel, requestFloor, setConnectedChannelsCount } =
    reachBackRequestsSliceHandler;
